import { ApiError, ApiErrorType } from './apiError';

export type CustomOptions = Omit<RequestInit, 'method' | 'body'>;
export type GetProps = {
  url: string;
  token?: string;
  customOptions?: CustomOptions;
  urlParams?: Record<string, string>;
  paginated?: boolean;
};
export const get = async <T>({
  url,
  token,
  customOptions,
  urlParams,
  paginated,
}: GetProps) => {
  const fetchUrl = new URL(url);
  const params = new URLSearchParams(urlParams);
  fetchUrl.search = params.toString();

  const getOptions = token
    ? createTokenOptions('GET', token)
    : defaultGetOptions;
  return fetchRequest<T>(
    fetchUrl.toString(),
    {
      ...getOptions,
      ...customOptions,
    },
    paginated
  );
};

type PostParams = {
  url: string;
  token?: string;
  body: RequestInit['body'];
  customOptions?: CustomOptions;
};

export const post = async <T>({
  url,
  token,
  body,
  customOptions,
}: PostParams) => {
  const postOptions = token
    ? createTokenOptions('POST', token)
    : defaultPostOptions;
  return fetchRequest<T>(url, {
    body,
    ...postOptions,
    ...customOptions,
  });
};

const fetchRequest = async <T>(
  url: string,
  { body, ...options }: RequestInit,
  paginated?: boolean
): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    body: JSON.stringify(body),
  });
  return handleResponse<T>(response, paginated);
};

const handleResponse = async <T>(
  response: Response,
  paginated: boolean = false
): Promise<T> => {
  const responseJson = await response.json();
  if (!response.ok) {
    if (response.status === 403) {
      let url = new URL(responseJson.error.redirectURL);
      url.searchParams.append('ReturnUrl', window.location.href);
      window.location.href = url.toString();
    }
    if (responseJson.hasError) {
      throw new ApiError(responseJson.error as ApiErrorType);
    }
    throw new Error(response.statusText);
  }
  if (responseJson.hasError) {
    throw new ApiError(responseJson.error as ApiErrorType);
  }
  if (paginated) {
    return responseJson;
  }
  return responseJson.data || responseJson;
};

export const getFile = async ({
  url,
  token,
  customOptions,
  urlParams,
}: GetProps) => {
  const fetchUrl = new URL(url);
  const params = new URLSearchParams(urlParams);
  fetchUrl.search = params.toString();

  const getOptions = token
    ? createTokenOptions('GET', token)
    : defaultGetOptions;
  return fetchFileRequest(fetchUrl.toString(), {
    ...getOptions,
    ...customOptions,
  });
};

const fetchFileRequest = async (
  url: string,
  { body, ...options }: RequestInit
) => {
  const response = await fetch(url, {
    ...options,
    body: JSON.stringify(body),
  });

  if (response.headers.get('content-type') === 'application/octet-stream') {
    // Because of CORS not exposing content-disposition, file name will probably be download.pdf
    // TODO: Fix above
    // TODO: If we ever need to get files, and not auto download, return block and move download to util function.
    const contentDisposition = response.headers.get('Content-Disposition');
    const fileName =
      contentDisposition?.split('filename=')[1].split(';')[0] ?? 'download.pdf';
    const responseBlob = await response.blob();
    let url = window.URL.createObjectURL(responseBlob);
    let a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    a.remove();
  }
  return;
};

const headers = {
  accept: 'application/json',
  'content-type': 'application/json',
};

const credentials: RequestCredentials = 'include';

const defaultGetOptions = {
  method: 'GET',
  credentials,
  headers,
};

const defaultPostOptions = {
  method: 'POST',
  credentials,
  headers,
};

const createTokenOptions = (method: 'GET' | 'POST', token: string) => {
  return {
    method,
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  };
};

export type PaginationType = {
  pageCount: number;
  pageNumber: number;
  itemCount?: number;
  itemNumber?: number;
};

export type Paginated<T> = {
  data: T[];
  pagination: PaginationType;
};
